/* FirmDetails — Firm Branding tab
   Fields: firm name, address, phone, email, website, SRA/FCA number.
   Saves all six keys in a single PUT. */
import { useEffect, useState } from 'react';
import css from '../../../AdminPage.module.css';
import { useFirmBranding, useUpdateFirmBranding } from '../../../../../hooks/useAdmin';
import { useToast } from '../../../../../hooks/useToast';

const KEYS = [
  'firm_name',
  'firm_address',
  'firm_phone',
  'firm_email',
  'firm_website',
  'firm_sra_number',
] as const;
type Key = (typeof KEYS)[number];
type Draft = Record<Key, string>;

const EMPTY: Draft = {
  firm_name: '',
  firm_address: '',
  firm_phone: '',
  firm_email: '',
  firm_website: '',
  firm_sra_number: '',
};

function isDirty(draft: Draft, original: Draft) {
  return KEYS.some((k) => draft[k] !== original[k]);
}

function validate(draft: Draft): string | null {
  const url = draft.firm_website.trim();
  if (url && !/^https?:\/\/.+/.test(url)) {
    return 'Website must start with http:// or https://';
  }
  return null;
}

export default function FirmDetails() {
  const { data: branding, isLoading } = useFirmBranding();
  const update = useUpdateFirmBranding();
  const { showToast } = useToast();

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [original, setOriginal] = useState<Draft>(EMPTY);

  /* Seed form from server data once loaded. */
  useEffect(() => {
    if (!branding) return;
    const seeded: Draft = {} as Draft;
    for (const k of KEYS) seeded[k] = branding[k] ?? '';
    setDraft(seeded);
    setOriginal(seeded);
  }, [branding]);

  function set(key: Key, value: string) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    const err = validate(draft);
    if (err) {
      showToast(err, 'error');
      return;
    }

    try {
      await update.mutateAsync(KEYS.map((k) => ({ key: k, value: draft[k].trim() || null })));
      showToast('Firm details saved', 'success');
      // Align original to saved state so dirty flag resets
      setOriginal({ ...draft });
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      showToast(msg || 'Failed to save firm details', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  const dirty = isDirty(draft, original);

  return (
    <div style={{ maxWidth: 680 }}>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)', marginBottom: 24 }}>
        Core details used in document templates, letter footers, and email signatures.
      </p>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Firm Name</label>
        <input
          data-testid="input-firm_name"
          className={css.formInput}
          value={draft.firm_name}
          onChange={(e) => set('firm_name', e.target.value)}
          placeholder="e.g. Smith &amp; Co Solicitors"
        />
      </div>

      <div className={css.formGroup}>
        <label className={css.formLabel}>Registered Address</label>
        <textarea
          data-testid="input-firm_address"
          className={css.formTextarea}
          rows={4}
          value={draft.firm_address}
          onChange={(e) => set('firm_address', e.target.value)}
          placeholder="Street, City, Postcode"
        />
      </div>

      <div className={css.grid2}>
        <div className={css.formGroup}>
          <label className={css.formLabel}>Phone Number</label>
          <input
            data-testid="input-firm_phone"
            className={css.formInput}
            type="tel"
            value={draft.firm_phone}
            onChange={(e) => set('firm_phone', e.target.value)}
            placeholder="e.g. +44 20 7123 4567"
          />
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--gray-500)',
              marginTop: 4,
              display: 'block',
            }}
          >
            Include country code for international format
          </span>
        </div>

        <div className={css.formGroup}>
          <label className={css.formLabel}>General Email</label>
          <input
            data-testid="input-firm_email"
            className={css.formInput}
            type="email"
            value={draft.firm_email}
            onChange={(e) => set('firm_email', e.target.value)}
            placeholder="e.g. enquiries@smithco.co.uk"
          />
        </div>
      </div>

      <div className={css.grid2}>
        <div className={css.formGroup}>
          <label className={css.formLabel}>Website</label>
          <input
            data-testid="input-firm_website"
            className={css.formInput}
            type="url"
            value={draft.firm_website}
            onChange={(e) => set('firm_website', e.target.value)}
            placeholder="https://www.smithco.co.uk"
          />
        </div>

        <div className={css.formGroup}>
          <label className={css.formLabel}>SRA / FCA Number</label>
          <input
            data-testid="input-firm_sra_number"
            className={css.formInput}
            value={draft.firm_sra_number}
            onChange={(e) => set('firm_sra_number', e.target.value)}
            placeholder="e.g. 123456"
          />
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--gray-500)',
              marginTop: 4,
              display: 'block',
            }}
          >
            Printed in letter footers and email signatures
          </span>
        </div>
      </div>

      <div style={{ marginTop: 8 }}>
        <button
          data-testid="btn-save"
          className={css.btnPrimary}
          onClick={handleSave}
          disabled={!dirty || update.isPending}
        >
          {update.isPending ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
